import { useCallback, useMemo } from 'react';
import { Navigate, useNavigate } from '@tanstack/react-router';

import { useGetPageProducts } from '@/api/hooks/queries/use-get-product-catalog-query';
import { Button } from '@/components/ui/button.tsx';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card.tsx';
import { useProductSelectionStep } from '@/hooks/use-product-selection-step';
import { useSessionSave } from '@/hooks/use-session-save';
import {
  addProductIds,
  removeProductIds,
  useSessionStore,
} from '@/hooks/use-session-store/use-session-store';
import { LOGGER_ACTIONS, logger } from '@/lib/logger';
import { PRODUCT_STEPS } from '@/lib/products.ts';
import { getActionablePlanFamilyCarrierTypes } from '@/types/insurance-builder.type.ts';
import { CATEGORY_TYPE } from '@/types/product.type.ts';
import ProductStepper from '@/views/layouts/ProductStepper.tsx';
import ProductCheckboxGroup from '@/views/products/components/ProductCheckboxGroup.tsx';
import { ProductSelectionSubmitFeedback } from '@/views/products/components/ProductSelectionSubmitFeedback.tsx';
import SkipButton from '@/views/products/components/SkipButton.tsx';

export default function SelectIncontinence() {
  const { saveSession } = useSessionSave();
  const { insuranceBuilder, selectedProductIds } = useSessionStore();

  const { products, isSuccess } = useGetPageProducts({
    categoryType: CATEGORY_TYPE.INCONTINENCE,
    patientCarrierTypes: getActionablePlanFamilyCarrierTypes(insuranceBuilder),
  });

  const updateProducts = useCallback(
    (updatedProductIds: string[]) => {
      const unselected = products.filter(
        (product) => !updatedProductIds.some((id) => id === product.uuid)
      );
      removeProductIds(unselected.map((p) => p.uuid));
      addProductIds(updatedProductIds);
    },
    [products]
  );

  const numProducts = useMemo(() => {
    return products.filter((p) => selectedProductIds.includes(p.uuid)).length;
  }, [products, selectedProductIds]);

  const navigate = useNavigate();
  const { nextStep } = useProductSelectionStep();
  const skipSelection = () => {
    removeProductIds(products.map((p) => p.uuid));
    // Auto-save session (non-blocking)
    saveSession();

    logger.action(LOGGER_ACTIONS.INTAKE.PRODUCT_SELECTION_SKIPPED, {
      product_category: CATEGORY_TYPE.INCONTINENCE,
    });
    navigate({ to: nextStep });
  };

  const handleAddToOrder = () => {
    // Auto-save session (non-blocking)
    saveSession();
    navigate({ to: nextStep });
  };

  const noEligibleProducts = isSuccess && products.length === 0;
  if (noEligibleProducts) {
    return <Navigate to={nextStep} replace />;
  }

  return (
    <section>
      <ProductStepper steps={PRODUCT_STEPS} />
      <div className="space-y-6">
        <Card spacing="sm">
          <CardHeader>
            <CardTitle>Choose incontinence supplies</CardTitle>
            <CardDescription>
              Refillable subscription—provider will help choose sizing & quantity.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <ProductCheckboxGroup
              allProducts={products}
              selectedProductIds={selectedProductIds}
              onSelectedChange={updateProducts}
            />
          </CardContent>
        </Card>
        <div className="grid grid-cols-[1fr_3fr] gap-3">
          <SkipButton categoryType={CATEGORY_TYPE.INCONTINENCE} onSkip={skipSelection} />
          <Button onClick={handleAddToOrder}>Add to order ({numProducts})</Button>
        </div>
        <ProductSelectionSubmitFeedback currentCategory={CATEGORY_TYPE.INCONTINENCE} />
      </div>
    </section>
  );
}
